// Address Step - Full Form (Kustom Style)
// All address fields in one clean form

import { useState, useCallback, useEffect } from 'react';
import { useCheckoutStore } from '../store/checkout';
import { lookupPostalCode } from '../api/client';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import styles from './AddressStep.module.css';

export function AddressStep() {
  const customer = useCheckoutStore((s) => s.customer);
  const shippingAddress = useCheckoutStore((s) => s.shippingAddress);
  const merchant = useCheckoutStore((s) => s.merchant);
  const setCustomer = useCheckoutStore((s) => s.setCustomer);
  const setShippingAddress = useCheckoutStore((s) => s.setShippingAddress);
  const nextStep = useCheckoutStore((s) => s.nextStep);
  const prevStep = useCheckoutStore((s) => s.prevStep);
  const isLoading = useCheckoutStore((s) => s.isLoading);
  const setLoading = useCheckoutStore((s) => s.setLoading);
  
  // Get postal code from store (set in previous step)
  const postalCode = shippingAddress?.postalCode || '';
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [careOf, setCareOf] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Lookup city from postal code on mount
  const lookupCity = useCallback(async () => {
    if (!postalCode || postalCode.length !== 4) return;
    
    try {
      const response = await lookupPostalCode(postalCode);
      if (response.data?.city) {
        setCity(response.data.city);
      }
    } catch (error) {
      console.error('Failed to lookup postal code:', error);
    }
  }, [postalCode]);

  useEffect(() => {
    lookupCity();
  }, [lookupCity]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Record<string, string> = {};
    
    if (!firstName.trim()) newErrors.firstName = 'Fornavn er påkrevd';
    if (!lastName.trim()) newErrors.lastName = 'Etternavn er påkrevd';
    if (!streetAddress.trim()) newErrors.streetAddress = 'Adresse er påkrevd';
    if (!city.trim()) newErrors.city = 'By eller sted er påkrevd';
    if (!phone.trim()) newErrors.phone = 'Mobiltelefonnummer er påkrevd';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Update customer with name
    setCustomer({
      ...customer!,
      name: `${firstName} ${lastName}`,
      phone,
    });
    
    // Set shipping address with postal code from store
    setShippingAddress({
      streetAddress,
      postalCode,
      city,
      countryCode: 'NO',
    });
    
    setLoading(false);
    nextStep();
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Dine opplysninger</h1>
      <p className={styles.subtitle}>
        Adressen må være i Norge. Felt som er merket med stjerne (*) må fylles ut.
      </p>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Email (read-only from previous step) */}
        <Input
          label="E-postadresse"
          type="email"
          value={customer?.email || ''}
          readOnly
          required
        />
        
        {/* Postal code (read-only from previous step) */}
        <Input
          label="Postnummer"
          type="text"
          value={postalCode}
          readOnly
          required
        />
        
        {/* Name row */}
        <div className={styles.inputRow}>
          <Input
            label="Fornavn"
            type="text"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setErrors(prev => ({ ...prev, firstName: '' }));
            }}
            error={errors.firstName}
            required
            autoComplete="given-name"
          />
          <Input
            label="Etternavn"
            type="text"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setErrors(prev => ({ ...prev, lastName: '' }));
            }}
            error={errors.lastName}
            required
            autoComplete="family-name"
          />
        </div>
        
        {/* C/O (optional) */}
        <Input
          label="C/O (valgfritt)"
          type="text"
          value={careOf}
          onChange={(e) => setCareOf(e.target.value)}
          autoComplete="off"
        />
        
        {/* Street address */}
        <Input
          label="Adresse"
          type="text"
          value={streetAddress}
          onChange={(e) => {
            setStreetAddress(e.target.value);
            setErrors(prev => ({ ...prev, streetAddress: '' }));
          }}
          error={errors.streetAddress}
          required
          autoComplete="street-address"
        />
        
        {/* City */}
        <Input
          label="By eller sted"
          type="text"
          value={city}
          onChange={(e) => {
            setCity(e.target.value);
            setErrors(prev => ({ ...prev, city: '' }));
          }}
          error={errors.city}
          required
          autoComplete="address-level2"
        />
        
        {/* Phone */}
        <Input
          label="Mobiltelefonnummer"
          type="tel"
          inputMode="numeric"
          value={phone}
          onChange={(e) => {
            const value = e.target.value.replace(/[^\d\s]/g, '');
            setPhone(value);
            setErrors(prev => ({ ...prev, phone: '' }));
          }}
          error={errors.phone}
          required
          autoComplete="tel"
        />

        <p className={styles.termsLink}>
          <a 
            href={merchant?.storeInfo?.termsAndConditionsURL || '#'}
            target="_blank"
            rel="noopener noreferrer"
          >
            Butikkens bruksvilkår gjelder
          </a> • <a href="#">Administrer autofylling</a>
        </p>

        <div className={styles.navigation}>
          <button type="button" className={styles.backButton} onClick={prevStep}>
            <BackIcon />
            Tilbake
          </button>
          <Button type="submit" loading={isLoading}>
            Fortsett
          </Button>
        </div>
      </form>
    </div>
  );
}

function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
